import { type Job, TraitementLong } from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';

interface UseTraitementLongData {
	/** Vrai du lancement à la fin du job : les boutons de la modale sont alors désactivés. */
	isEnCours: boolean;
	lancer: (lancement: () => Promise<Job>) => Promise<void>;
	traitementComponent: React.ReactNode;
	/**
	 * Fermeture de la modale qui suit le job, à étaler sur elle (`<Modal {...fermeture(onClose)}>`) : impossible
	 * pendant le job (croix masquée, Échap et clic à côté sans effet). Fermée en cours de route, la modale perdrait
	 * le suivi du job et l'arbre ne serait pas mis à jour à sa fin.
	 */
	fermeture: (onClose: VoidFunction) => {
		closeIcon: boolean;
		onClose: VoidFunction | undefined;
	};
}

/**
 * Traitement long suivi par son Job360 (numérotation, dénumérotation d'un bordereau), comme le rejet : `lancer`
 * appelle la route qui renvoie le Job360, `traitementComponent` affiche sa progression ; à la fin du job, `onSucces`
 * s'il a réussi, sinon le message de l'erreur enregistré par l'API.
 */
interface UseTraitementLongOptions {
	/** Rendu d'une erreur au lancement (ex. message sur plusieurs lignes) ; par défaut, le message de l'API. */
	renduErreurLancement?: (erreur: unknown) => React.ReactNode;
}

const useTraitementLong = (
	titre: string,
	messageErreur: string,
	onSucces: (job: Job) => Promise<void> | void,
	{ renduErreurLancement }: UseTraitementLongOptions = {},
): UseTraitementLongData => {
	const [job, setJob] = React.useState<Job | null>(null);
	const [isEnCours, setIsEnCours] = React.useState(false);

	const lancer = async (lancement: () => Promise<Job>): Promise<void> => {
		setIsEnCours(true);
		try {
			setJob(await lancement());
		} catch (error) {
			// ex. bordereau déjà en cours de traitement : l'API dit depuis quand et quand il pourra être relancé
			setIsEnCours(false);
			toast.error(
				renduErreurLancement?.(error) ?? getErrorMessage(error, messageErreur),
			);
		}
	};

	const handleFinish = async (jobTermine: Job): Promise<void> => {
		setJob(null);
		try {
			if (jobTermine.status === 1) {
				await onSucces(jobTermine);
			} else {
				toast.error(jobTermine.resultat);
			}
		} catch (error) {
			toast.error(getErrorMessage(error, messageErreur));
		} finally {
			setIsEnCours(false);
		}
	};

	const handleError = (): void => {
		setJob(null);
		setIsEnCours(false);
		toast.error(messageErreur);
	};

	const traitementComponent = job && (
		<TraitementLong
			title={titre}
			identifiantJob={job.identifiant}
			onFinish={handleFinish}
			onError={handleError}
		/>
	);

	const fermeture = (onClose: VoidFunction) => ({
		closeIcon: !isEnCours,
		onClose: isEnCours ? undefined : onClose,
	});

	return { isEnCours, lancer, traitementComponent, fermeture };
};

export default useTraitementLong;
